import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import type { JSX, MouseEvent, ReactNode, SyntheticEvent } from 'react';

import { Button, type ButtonProps } from '../../../Button/Button.tsx';
import { DialogTitle } from '../../DialogTitle/DialogTitle.tsx';

export interface ConfirmationDialogProps {
  title: ReactNode;
  isOpen: boolean;
  isLoading?: boolean;
  onConfirmed: () => void;
  onCancel: () => void;
  onExtraAction?: () => void;
  children: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  extraBtnLabel?: ReactNode;
  customContent?: boolean;
  confirmationButtonColor?: ButtonProps['color'];
  confirmationDisabled?: boolean;
  hideConfirm?: boolean;
  showExtraBtn?: boolean;
  extraButtonColor?: ButtonProps['color'];
  actionsLeftContent?: ReactNode;
}

export function ConfirmationDialog(props: ConfirmationDialogProps): JSX.Element {
  const {
    title,
    isOpen,
    onConfirmed,
    onCancel,
    onExtraAction,
    children,
    isLoading,
    confirmLabel = 'Ok',
    cancelLabel = 'Cancel',
    customContent = false,
    confirmationButtonColor = 'primary',
    confirmationDisabled = false,
    hideConfirm = false,
    showExtraBtn = false,
    extraBtnLabel = 'Extra',
    extraButtonColor = 'secondary',
    actionsLeftContent,
  } = props;

  const handleOkButton = (event: MouseEvent): void => {
    event.stopPropagation();
    onConfirmed();
  };

  const handleCancelButton = (event: MouseEvent): void => {
    event.stopPropagation();
    onCancel();
  };

  const handleExtraButton = (event: MouseEvent): void => {
    event.stopPropagation();
    if (onExtraAction) onExtraAction();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={(event: SyntheticEvent<HTMLElement>, reason) => {
        if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
          event.stopPropagation();
        }
        onCancel();
      }}
      data-test={props['data-test']}
      onClick={e => e.stopPropagation()}
    >
      <DialogTitle dataTest="confirmation-dialog-title" onClose={onCancel} onCloseDisabled={isLoading}>
        {title}
      </DialogTitle>
      {customContent ? (
        children
      ) : (
        <DialogContent data-test="dialog-content" dividers>
          <DialogContentText>{children}</DialogContentText>
        </DialogContent>
      )}
      <DialogActions
        data-test="dialog-actions"
        sx={actionsLeftContent ? { justifyContent: 'space-between', px: 2, pb: 2 } : undefined}
      >
        {actionsLeftContent && <Box sx={{ display: 'flex', alignItems: 'center' }}>{actionsLeftContent}</Box>}
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Button onClick={handleCancelButton} variant="text" disabled={isLoading} data-test="cancel-button">
            {cancelLabel}
          </Button>

          {showExtraBtn && (
            <Button onClick={handleExtraButton} disabled={isLoading} data-test="extra-button" color={extraButtonColor}>
              {extraBtnLabel}
            </Button>
          )}

          {!hideConfirm && (
            <Button
              onClick={handleOkButton}
              color={confirmationButtonColor}
              disabled={isLoading || confirmationDisabled}
              loading={isLoading}
              autoFocus
              data-test="submit-button"
            >
              {confirmLabel}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
